import { useEffect, useState, Fragment, useContext } from "react";
import { ref, onValue, push, update, remove } from "firebase/database";
import { database } from "./firebase";
import DataTable from "react-data-table-component";
import Select from "react-select";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  Nav,
  Tab,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import SearchBar from "./searchBar";
import TableStyleCard from "./tableStyleCard";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Cookies from "js-cookie";
import { filterTicketsByRole } from "./filterTickets";
import { AuthContext } from "./AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faDownload,
  faEdit,
  faEllipsisH,
  faEye,
  faFileCsv,
  faFileExcel,
  faMessage,
  faPrint,
  faRotateBack,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { sendEmail } from "./sendEmail";
import { ticketEmailBody } from "./ticketEmailBody";
import { ticketStatusUpdateEmailBody } from "./ticketStatusUpdateEmailBody";

// Helper function to format date
const formatDate = (isoString) => {
  if (!isoString) return "-";
  return new Date(isoString).toLocaleString();
};

// Helper function to calculate duration between two ISO strings
const calculateDuration = (timeRaised, dateResolved) => {
  if (!timeRaised || !dateResolved) return "-";

  const start = new Date(timeRaised);
  const end = new Date(dateResolved);
  const diffMs = end - start;

  if (diffMs < 0) return "Invalid Dates";

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  let result = [];
  if (diffDays > 0) result.push(`${diffDays}d`);
  if (diffHours > 0) result.push(`${diffHours}h`);
  if (diffMinutes > 0 || result.length === 0) result.push(`${diffMinutes}m`); // Always show minutes if no days/hours

  return result.join(" ");
};

const Tickets = () => {
  const { role, userId, email, fullname } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);

  const [attachments, setAttachments] = useState([]);

  const [showCreateUpdateModal, setShowCreateUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showfilterTicketsModal, setShowFilterTicketsModal] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [currentTicket, setCurrentTicket] = useState(null);
  const [progressPending, setProgressPending] = useState(false);

  // States for Filter Sheet and Filter Criteria
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({
    searchQuery: "",
    status: "",
    raisedBy: "",
    assignedTo: "",
    dateFrom: "",
    dateTo: "",
  });

  const [formData, setFormData] = useState({
    id: "",
    subject: "",
    department: "",
    assignedUsers: [],
    deadline: "",
    priority: "",
    status: "Open",
    requestType: "",
    category: "",
    additionalInfo: "",
    raisedBy: "Admin",
    timeRaised: "",
    progress: 0,
    dateResolved: null,
  });

  const navigate = useNavigate();

  // --- Data Fetching Effect ---
  useEffect(() => {
    const tRef = ref(database, "/helpdesk/tickets");
    const dRef = ref(database, "/helpdesk/departments");
    const cRef = ref(database, "/helpdesk/categories");
    const uRef = ref(database, "/helpdesk/users");

    // ... (inside the useEffect or wherever you listen for tickets)

    const unsubT = onValue(tRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // 1. Convert snapshot data into a list array
        let list = Object.keys(data).map((id) => ({
          id,
          ...data[id],
          assignedUsers: data[id].assignedUsers || [],
        }));

        // 2. Apply Role-Based Filtering
        list = filterTicketsByRole(list, userId, role);

        // 3. Update the component state
        setTickets(list);
      } else {
        setTickets([]);
      }
    });
    onValue(dRef, (snap) => {
      const d = snap.val();
      if (d) {
        const list = Object.keys(d).map((id) => ({ id, ...d[id] }));
        setDepartments(list);
      }
    });

    onValue(cRef, (snap) => {
      const c = snap.val();
      if (c) {
        const list = Object.keys(c).map((id) => ({ id, ...c[id] }));
        setCategories(list);
      }
    });

    onValue(uRef, (snap) => {
      const u = snap.val();

      if (u) {
        const list = Object.keys(u).map((id) => {
          const image =
            u[id].profilePicture ||
            `https://api.dicebear.com/7.x/avataaars/svg?seed=${id}&backgroundColor=b6e3f4,c0aede,d1d4f9&radius=50`;

          return {
            id,
            ...u[id],
            label: u[id].fullname,
            value: id,
            image, // ✅ just a URL
          };
        });

        setUsers(list);
      }
    });

    return () => {
      unsubT();
    };
  }, []);

  // --- Filtering Logic ---
  useEffect(() => {
    let result = tickets;

    // 1. Apply text search filter
    if (filterCriteria.searchQuery) {
      const query = filterCriteria.searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.subject?.toLowerCase().includes(query) ||
          t.status?.toLowerCase().includes(query)
      );
    }

    // 2. Apply advanced filters
    if (filterCriteria.status) {
      result = result.filter((t) => t.status === filterCriteria.status);
    }
    if (filterCriteria.raisedBy) {
      result = result.filter((t) => t.raisedBy === filterCriteria.raisedBy);
    }
    if (filterCriteria.assignedTo) {
      result = result.filter((t) =>
        t.assignedUsers?.includes(filterCriteria.assignedTo)
      );
    }
    if (filterCriteria.dateFrom) {
      result = result.filter(
        (t) => new Date(t.timeRaised) >= new Date(filterCriteria.dateFrom)
      );
    }
    if (filterCriteria.dateTo) {
      const dateTo = new Date(filterCriteria.dateTo);
      dateTo.setDate(dateTo.getDate() + 1);
      result = result.filter((t) => new Date(t.timeRaised) <= dateTo);
    }

    setFilteredTickets(result);
  }, [tickets, filterCriteria]);

  // --- Filter and Search Handlers ---
  const handleSearch = (query) => {
    setFilterCriteria((prev) => ({ ...prev, searchQuery: query }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterCriteria((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilterCriteria((prev) => ({
      searchQuery: prev.searchQuery,
      status: "",
      raisedBy: "",
      assignedTo: "",
      dateFrom: "",
      dateTo: "",
    }));
  };

  // --- Form Data Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  //get users emails = [{ id, fullname, email, ... }]

  const getEmailsByUserIds = (userIds = []) => {
    return users
      .filter((u) => userIds.includes(u.id))
      .map((u) => u.email)
      .filter(Boolean); // remove undefined/null
  };

  const handleAssignedUsers = (selected) => {
    setFormData((prev) => ({
      ...prev,
      assignedUsers: selected.map((s) => s.value),
    }));
  };

  const getUsersByDepartment = () => {
    const dept = formData.department;
    if (!dept) return [];
    return users.filter((u) => u.department === dept);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const uploaded = files.map((file) => ({
      name: file.name,
      size: `${(file.size / 1024).toFixed(1)} KB`,
      url: URL.createObjectURL(file),
    }));
    setAttachments((prev) => [...prev, ...uploaded]);
  };

  // --- CRUD/Action Functions ---
  const startCreate = () => {
    setFormData({
      id: "",
      subject: "",
      department: "",
      assignedUsers: [],
      deadline: "",
      priority: "",
      status: "Open",
      requestType: "",
      category: "",
      additionalInfo: "",
      raisedBy: userId,
      timeRaised: new Date().toISOString(),
      progress: 0,
      dateResolved: null,
    });
    setAttachments([]);
    setActiveTab("info");
    setShowCreateUpdateModal(true);
  };

  const startUpdate = (ticket) => {
    setFormData(ticket);
    setAttachments([]);
    setActiveTab("info");
    setShowCreateUpdateModal(true);
  };

  const startView = (ticket) => {
    setCurrentTicket(ticket);
    setShowViewModal(true);
    setActiveTab("info");
  };

  const saveTicket = async (e) => {
    e.preventDefault();
    setProgressPending(true);

    const payload = {
      ...formData,
      createdBy: userId,
      createdByEmail: email,
      createdAt: new Date().toISOString(),
    };

    delete payload.id;

    // Emails of assigned users
    const assignedEmails = getEmailsByUserIds(formData.assignedUsers);

    let ticketId = formData.id;

    if (formData.id) {
      // UPDATE ticket
      const tUpdate = ref(database, `/helpdesk/tickets/${formData.id}`);
      await update(tUpdate, payload);
    } else {
      // CREATE ticket
      const tRef = ref(database, "/helpdesk/tickets");
      const newRef = await push(tRef, payload);
      ticketId = newRef.key;

      // Save attachments
      if (attachments.length > 0) {
        const aRef = ref(database, `/helpdesk/attachments/${ticketId}`);

        for (const file of attachments) {
          await push(aRef, {
            name: file.name,
            url: file.url,
            size: file.size,
            uploadedAt: new Date().toISOString(),
          });
        }
      }

      // 🔔 Send email notification
      if (assignedEmails.length > 0 || email) {
        await sendEmail({
          from: "support@helpdesk.greenwellsenergies.co.ke",
          to: assignedEmails,
          cc: email ? [email] : [], // ticket owner
          subject: `New Ticket Created: #${ticketId}- ${formData.subject}`,
          message: ticketEmailBody({
            fullname: fullname,
            ticketId,
            subject: payload.subject,
            description: formData.additionalInfo,
            department: payload.department,
            priority: payload.priority,
            createdAt: new Date(payload.createdAt).toLocaleString(),
          }),
          isHtml: true,
        });
      }
    }
    setProgressPending(false);
    setShowCreateUpdateModal(false);
  };

  const deleteTicket = async (id, status) => {
    if (status === "Resolved") return alert("Cannot delete a resolved ticket.");
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;
    const tRef = ref(database, `/helpdesk/tickets/${id}`);
    await remove(tRef);
  };

  const markResolved = async (ticket) => {
    if (ticket.status === "Resolved") return;
    if (!window.confirm("Mark this ticket as Resolved?")) return;

    const resolvedAt = new Date().toISOString();

    const tUpdate = ref(database, `/helpdesk/tickets/${ticket.id}`);
    await update(tUpdate, {
      status: "Resolved",
      progress: 100,
      dateResolved: resolvedAt,
    });

    // 🔔 EMAIL NOTIFICATION (non-blocking)
    try {
      // Resolve assigned users' emails
      const assignedEmails = users
        .filter((u) => ticket.assignedUsers?.includes(u.id))
        .map((u) => u.email)
        .filter(Boolean);

      // Ticket owner email
      const ownerEmail = ticket.createdByEmail;

      const user = users.find((u) => u.id === ticket.raisedBy);

      await sendEmail({
        from: "support@helpdesk.greenwellsenergies.co.ke",
        to: ownerEmail ? [ownerEmail] : [],
        cc: assignedEmails,
        subject: `Ticket Resolved: #${ticket.id}- ${ticket.subject}`,
        message: ticketStatusUpdateEmailBody({
          fullname: user?.fullname || "User",
          ticketId: ticket.id,
          subject: ticket.subject,
          oldStatus: ticket.status,
          description: ticket.additionalInfo,
          newStatus: "Resolved",
          updatedBy: fullname,
          updatedAt: new Date(resolvedAt).toLocaleString(),
        }),
        isHtml: true,
      });
    } catch (err) {
      console.error("Failed to send resolution email:", err);
    }
  };

  // Function to update the ticket status and remove dateResolved
  const reopenTicket = async (row) => {
    const ticketId = row.id;

    // 1. Authorization Check
    if (row.raisedBy !== userId) {
      alert(
        "You are not authorized to reopen this ticket. Only the user who created it can."
      );
      return;
    }

    try {
      const updatedAt = new Date().toISOString();

      // Update ticket in Firebase
      const ticketPath = `/helpdesk/tickets/${ticketId}`;
      const tRef = ref(database, ticketPath);

      await update(tRef, {
        status: "Open",
        dateResolved: null,
        dateUpdated: updatedAt,
      });

      // Update local state
      setTickets((prevTickets) =>
        prevTickets.map((t) =>
          t.id === ticketId
            ? {
                ...t,
                status: "Open",
                dateResolved: null,
                dateUpdated: updatedAt,
              }
            : t
        )
      );

      // 🔔 EMAIL NOTIFICATION (non-blocking)
      try {
        const user = users.find((u) => u.id === row.raisedBy);

        const assignedEmails = users
          .filter((u) => row.assignedUsers?.includes(u.id))
          .map((u) => u.email)
          .filter(Boolean);

        // Owner email
        const ownerEmail = user?.email;

        console.log(fullname);

        if (ownerEmail || assignedEmails.length > 0) {
          await sendEmail({
            from: "support@helpdesk.greenwellsenergies.co.ke",
            to: ownerEmail ? [ownerEmail] : [],
            cc: assignedEmails,
            subject: `Ticket Reopened: #${ticketId}`,
            message: ticketStatusUpdateEmailBody({
              fullname: user?.fullname || "User",
              ticketId,
              subject: row.subject,
              oldStatus: "Resolved",
              newStatus: "Open",
              description: row.additionalInfo,
              updatedBy: fullname,
              updatedAt: new Date(updatedAt).toLocaleString(),
            }),
            isHtml: true,
          });
        }
      } catch (mailErr) {
        console.error("Failed to send reopen email:", mailErr);
      }

      console.log(`Ticket ${ticketId} reopened successfully.`);
    } catch (error) {
      console.error("Error reopening ticket: ", error);
    }
  };

  const getDisplayName = (raisedById) => {
    if (raisedById === userId) {
      return "You";
    }

    const user = users.find((u) => u.id === raisedById);

    return user ? user.fullname : "N/A";
  };

  //get user image
  const getUserImageByUid = (uid) => {
    const user = users.find((u) => u.id === uid);

    return user?.profilePicture
      ? user.profilePicture
      : `https://api.dicebear.com/7.x/avataaars/svg?seed=${uid}`;
  };

  // --- Export and Print Handlers ---
  const handleExport = (format) => {
    const dataToExport = filteredTickets.map((t) => ({
      ID: t.id,
      Subject: t.subject,
      Status: t.status,
      Progress: t.progress,
      Priority: t.priority,
      Department: t.department,
      "Raised By":
        users.find((u) => u.id === t.raisedBy)?.fullname || t.raisedBy,
      "Time Raised": formatDate(t.timeRaised),
      Deadline: t.deadline,
      "Date Resolved": formatDate(t.dateResolved),
      Duration: calculateDuration(t.timeRaised, t.dateResolved), // New Duration field
    }));

    if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tickets");
      XLSX.writeFile(wb, "Tickets_Export.xlsx");
    } else if (format === "csv") {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Tickets_Export.csv";
      link.click();
    }
  };

  const handlePrint = (ticket) => {
    const doc = new jsPDF();
    const margin = 15;
    let y = margin;
    const lineHeight = 7;
    const pageWidth = doc.internal.pageSize.getWidth();

    // Dummy Logo (Text)
    const logoText = "GWEL ADMIN TICKETING";
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text(logoText, margin, y + 5);
    doc.setFont(undefined, "normal");

    y += lineHeight * 2;

    // Title: Ticket Type (using ticket.requestType if available)
    doc.setFontSize(18);
    doc.text(
      `${ticket.requestType || "Ticket"} Report: ${ticket.subject}`,
      pageWidth / 2,
      y,
      { align: "center" }
    );
    y += lineHeight * 2;

    // Ticket Details Table
    doc.setFontSize(10);
    const details = [
      ["ID", ticket.id],
      ["Subject", ticket.subject],
      ["Status", ticket.status],
      ["Priority", ticket.priority],
      ["Progress", `${ticket.progress}%`],
      [
        "Raised By",
        users.find((u) => u.id === ticket.raisedBy)?.fullname ||
          ticket.raisedBy,
      ],
      ["Time Raised", formatDate(ticket.timeRaised)],
      ["Department", ticket.department],
      ["Deadline", ticket.deadline],
      [
        "Assigned Users",
        (ticket.assignedUsers || [])
          .map((id) => users.find((u) => u.id === id)?.fullname || id)
          .join(", "),
      ],
      ["Additional Info", ticket.additionalInfo],
    ];

    details.forEach(([label, value]) => {
      doc.text(`${label}:`, margin, y);
      doc.text(value || "-", margin + 40, y);
      y += lineHeight;
    });

    // Approved By Section
    y += lineHeight * 3;
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;

    doc.setFontSize(12);
    doc.text("Approved By:", margin, y);
    y += lineHeight;
    doc.text("Name: _______________________________", margin, y);
    y += lineHeight;
    doc.text("Signature: ____________________________", margin, y);
    y += lineHeight;
    doc.text("Date: ________________________________", margin, y);

    doc.save(`Ticket_${ticket.id}.pdf`);
  };

  // --- Data Table Columns ---
  const columns = [
    {
      name: "Subject",
      selector: (row) => (
        <div className="d-flex flex-column align-items-start py-2">
          <div className="d-flex align-items-center" style={{ width: "400px" }}>
            <span className="text-gray-800" style={{ fontSize: "1.2rem" }}>
              {row.subject}
            </span>
            <span className="text-gray-500 ml-auto">
              {formatDate(row.timeRaised)}
            </span>
          </div>
          <div>
            <div className="bg-light p-3 rounded d-flex flex-column align-items-center">
              <img
                src={getUserImageByUid(row.raisedBy)}
                alt="User"
                width={48}
                height={48}
                className="rounded-circle mb-1 bg-white shadow p-1"
                style={{ objectFit: "cover" }}
              />

              <span className="h5 text-gray-500 mb-0">
                {getDisplayName(row.raisedBy)}
              </span>
            </div>
          </div>
        </div>
      ),
      sortable: true,
    },

    {
      name: "Actions",
      right: true,
      cell: (row) => (
        <div className="dropdown">
          <button
            className="btn btn-round"
            type="button"
            data-toggle="dropdown"
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
          <div className="dropdown-menu dropdown-menu-right shadow-lg border-0">
            <button
              className="dropdown-item"
              onClick={(e) => {
                e.stopPropagation();
                startView(row);
              }}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faEye} />
              View
            </button>

            <button
              className="dropdown-item"
              onClick={(e) => {
                e.stopPropagation();
                startUpdate(row);
              }}
              disabled={row.status === "Resolved"}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faEdit} />
              Update
            </button>

            <button
              className="dropdown-item"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faMessage} />
              Remarks
            </button>

            <button
              className="dropdown-item"
              onClick={(e) => {
                e.stopPropagation();
                handlePrint(row);
              }}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faPrint} />
              Print PDF
            </button>

            {row.status !== "Resolved" ? (
              <button
                className="dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  markResolved(row);
                }}
              >
                <FontAwesomeIcon
                  className="text-gray-300"
                  icon={faCheckCircle}
                />
                Mark Resolved
              </button>
            ) : (
              row.raisedBy === userId && (
                <button
                  className="dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    reopenTicket(row);
                  }}
                >
                  <FontAwesomeIcon
                    className="text-gray-300"
                    icon={faRotateBack}
                  />
                  Reopen
                </button>
              )
            )}

            <button
              className={`dropdown-item text-danger ${
                row.status === "Resolved" ? "disabled" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                deleteTicket(row.id, row.status);
              }}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faTrash} />
              Delete
            </button>
          </div>
        </div>
      ),
      width: "140px",
    },
  ];

  return (
    <>
      <div className="card mt-5 shadow p-3 d-flex align-items-center flex-row justify-content-between">
        <h3 className="text-gray-800">
          {filteredTickets.length} record
          {filteredTickets.length !== 1 ? "s" : ""}
        </h3>
        {/* Filter & Export Button Group */}
        <div className="dropdown">
          <button
            className="btn btn-light"
            id="btnGroupExport"
            type="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            title="Export"
          >
            <FontAwesomeIcon className="text-gray-500" icon={faDownload} />
            Export data
          </button>
          <div
            className="dropdown-menu dropdown-menu-right shadow-lg border-0"
            aria-labelledby="btnGroupExport"
          >
            <h6 className="dropdown-header">Export Data</h6>
            <button
              className="dropdown-item"
              onClick={() => handleExport("excel")}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faFileExcel} />
              Excel
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleExport("csv")}
            >
              <FontAwesomeIcon className="text-gray-300" icon={faFileCsv} />
              CSV
            </button>
          </div>
        </div>
      </div>
      <div
        className="mt-5"
        style={{
          opacity: showFilterSheet ? 0.5 : 1,
          transition: "opacity 0.3s",
        }}
      >
        <div>
          <div className="card shadow">
            <div className="card-header bg-white py-2 d-flex align-items-center">
              <div className="d-flex w-100">
                <SearchBar onSearch={handleSearch} />

                <button
                  className="ml-auto btn btn-lg btn-light mr-2"
                  onClick={setShowFilterTicketsModal}
                >
                  Filter tickets
                </button>

                <button
                  className="btn btn-lg btn-primary"
                  onClick={startCreate}
                >
                  Add Ticket
                </button>
              </div>
            </div>
            <div className="p-2" style={{ background: "#E1E3F0" }}>
              <DataTable
                customStyles={TableStyleCard}
                noTableHead
                columns={columns}
                data={filteredTickets}
                highlightOnHover
                conditionalRowStyles={[
                  {
                    when: () => true,
                    style: {
                      animation: "fadeInUp 0.5s ease-in-out",
                    },
                  },
                ]}
                onRowClicked={(row) => startView(row)}
                pagination
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- 1. Create/Update Modal --- */}
      <Modal
        show={showCreateUpdateModal}
        size="lg"
        backdrop="static"
        onHide={() => setShowCreateUpdateModal(false)}
      >
        <ModalHeader closeButton>
          <ModalTitle>
            {formData.id ? "Update Ticket" : "Create Ticket"}
          </ModalTitle>
        </ModalHeader>
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav variant="tabs" fill>
            <Nav.Item>
              <Nav.Link eventKey="info">Ticket Information</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="attachments">Add Attachments</Nav.Link>
            </Nav.Item>
          </Nav>

          <ModalBody>
            <Tab.Content className="mt-3 px-1">
              {/* Ticket Info Tab */}
              <Tab.Pane eventKey="info">
                <form onSubmit={saveTicket}>
                  {[
                    ["Subject", "subject", "text"],
                    ["Department", "department", "select"],
                    ["Category", "category", "select"],
                    ["Priority", "priority", "select"],
                    ["Request Type", "requestType", "select"],
                    ["Deadline", "deadline", "date"],
                  ].map(([label, name, type], i) => (
                    <div className="form-group row mb-3" key={i}>
                      <label className="col-3 col-form-label">{label}</label>
                      <div className="col">
                        {type === "select" ? (
                          <select
                            className="form-control"
                            name={name}
                            value={formData[name]}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select {label}</option>
                            {name === "department" &&
                              departments.map((d) => (
                                <option key={d.id} value={d.name}>
                                  {d.name}
                                </option>
                              ))}
                            {name === "category" &&
                              categories.map((c) => (
                                <option key={c.id} value={c.name}>
                                  {c.name}
                                </option>
                              ))}
                            {name === "priority" &&
                              ["Low", "High", "Urgent"].map((p) => (
                                <option key={p}>{p}</option>
                              ))}
                            {name === "requestType" &&
                              ["Jobcard", "Task Request"].map((r) => (
                                <option key={r}>{r}</option>
                              ))}
                          </select>
                        ) : (
                          <input
                            type={type}
                            name={name}
                            value={formData[name]}
                            onChange={handleChange}
                            className="form-control"
                            required
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Assigned Users */}
                  {formData.department && (
                    <div className="form-group row mb-3">
                      <label className="col-3 col-form-label">
                        Assigned Users
                      </label>
                      <div className="col">
                        <Select
                          isMulti
                          options={getUsersByDepartment().map((u) => ({
                            value: u.id,
                            label: (
                              <div className="d-flex align-items-center">
                                <img
                                  src={u.image}
                                  alt=""
                                  width="24"
                                  height="24"
                                  className="rounded-circle mr-2"
                                />
                                <span>{u.label}</span>
                              </div>
                            ),
                          }))}
                          onChange={handleAssignedUsers}
                          placeholder="Assign Users"
                          value={(formData.assignedUsers || [])
                            .map((userId) => users.find((u) => u.id === userId))
                            .filter(Boolean)
                            .map((u) => ({ value: u.id, label: u.fullname }))}
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group row mb-3">
                    <label className="col-3 col-form-label">
                      Additional Info
                    </label>
                    <div className="col">
                      <textarea
                        className="form-control"
                        name="additionalInfo"
                        rows="3"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </form>
              </Tab.Pane>

              {/* Attachments Tab */}
              <Tab.Pane eventKey="attachments">
                <div className="mb-3">
                  <input
                    type="file"
                    multiple
                    className="form-control"
                    onChange={handleFileUpload}
                  />
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "60%" }}>File Name</th>
                        <th style={{ width: "25%" }}>Preview</th>
                        <th style={{ width: "15%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.length > 0 ? (
                        attachments.map((file, idx) => (
                          <tr key={idx} className="mb-3">
                            <td>{file.name}</td>
                            <td>
                              <img
                                src={file.url}
                                alt={file.name}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                }}
                              />
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-round"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  lineHeight: "1",
                                }}
                                onClick={() =>
                                  setAttachments((prev) =>
                                    prev.filter((_, i) => i !== idx)
                                  )
                                }
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            No attachments
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </ModalBody>
          <ModalFooter>
            <button
              type="submit"
              onClick={saveTicket}
              className="btn btn-primary px-4"
              disabled={progressPending} // disable while loading
            >
              {progressPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {formData.id ? "Updating..." : "Creating..."}
                </>
              ) : formData.id ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
          </ModalFooter>
        </Tab.Container>
      </Modal>

      {/* --- 2. View Ticket Modal --- */}
      <Modal
        show={showViewModal}
        size="lg"
        backdrop="static"
        onHide={() => setShowViewModal(false)}
      >
        <ModalHeader closeButton>
          <ModalTitle>View Ticket: {currentTicket?.subject}</ModalTitle>
        </ModalHeader>
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav variant="tabs" fill>
            <Nav.Item>
              <Nav.Link eventKey="info">Ticket Information</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="attachments">Attachments</Nav.Link>
            </Nav.Item>
          </Nav>
          <ModalBody>
            <Tab.Content className="mt-3 px-1">
              {/* Ticket Information Table View */}
              <Tab.Pane eventKey="info">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle mb-0">
                    <tbody>
                      <tr>
                        <td className="col-3 font-weight-bold">ID</td>
                        <td>{currentTicket?.id}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Subject</td>
                        <td>{currentTicket?.subject}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Status</td>
                        <td>{currentTicket?.status}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Priority</td>
                        <td>{currentTicket?.priority}</td>
                      </tr>

                      <tr>
                        <td className="col-3 font-weight-bold">Raised By</td>
                        <td>{currentTicket?.raisedBy}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Time Raised</td>
                        <td>{formatDate(currentTicket?.timeRaised)}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">
                          Date Resolved
                        </td>
                        <td>{formatDate(currentTicket?.dateResolved)}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Department</td>
                        <td>{currentTicket?.department}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Category</td>
                        <td>{currentTicket?.category}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Request Type</td>
                        <td>{currentTicket?.requestType}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">Deadline</td>
                        <td>{currentTicket?.deadline}</td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">
                          Assigned Users
                        </td>
                        <td>
                          {(currentTicket?.assignedUsers || [])
                            .map(
                              (userId) =>
                                users.find((u) => u.id === userId)?.fullname ||
                                userId
                            )
                            .join(", ")}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-3 font-weight-bold">
                          Additional Info
                        </td>
                        <td>{currentTicket?.additionalInfo || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Tab.Pane>

              {/* Attachments Tab */}
              <Tab.Pane eventKey="attachments">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "60%" }}>File Name</th>
                        <th style={{ width: "40%" }}>Preview / Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.length > 0 ? (
                        attachments.map((file, idx) => (
                          <tr key={idx}>
                            <td>
                              {file.name} ({file.size})
                            </td>
                            <td>
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {file.url.includes("blob:")
                                  ? "Preview/Download"
                                  : "Download"}
                              </a>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="text-center text-muted">
                            No attachments
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </ModalBody>
          <ModalFooter>
            <button
              className="btn btn-secondary"
              onClick={() => setShowViewModal(false)}
            >
              Close
            </button>
          </ModalFooter>
        </Tab.Container>
      </Modal>

      {/* --- 4. Filter tickets --- */}
      <Modal
        show={showfilterTicketsModal}
        size="lg"
        onHide={() => setShowFilterTicketsModal(false)}
        backdrop="static"
      >
        <ModalHeader closeButton>
          <ModalTitle>Filter Tickets</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <form>
            <div className="form-group mb-3">
              <label>Status</label>
              <select
                className="form-control"
                name="status"
                value={filterCriteria.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
              </select>
            </div>

            <div className="form-group mb-3">
              <label>Raised By</label>
              <select
                className="form-control"
                name="raisedBy"
                value={filterCriteria.raisedBy}
                onChange={handleFilterChange}
              >
                <option value="">Raised by</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullname}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mb-3">
              <label>Assigned To</label>
              <select
                className="form-control"
                name="assignedTo"
                value={filterCriteria.assignedTo}
                onChange={handleFilterChange}
              >
                <option value="">All Assigned Users</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullname}
                  </option>
                ))}
              </select>
            </div>

            <h6 className="mt-4 mb-3">Date Range (Time Raised)</h6>
            <div className="form-group mb-3">
              <label>From Date</label>
              <input
                type="date"
                className="form-control"
                name="dateFrom"
                value={filterCriteria.dateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group mb-3">
              <label>To Date</label>
              <input
                type="date"
                className="form-control"
                name="dateTo"
                value={filterCriteria.dateTo}
                onChange={handleFilterChange}
              />
            </div>
          </form>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary mr-auto"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowFilterTicketsModal(false)}
          >
            Apply & Close
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Tickets;
